import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { FormModel } from '@vincerosoft/assessfluence-types';
import { where } from 'firebase/firestore';

type LegacyFormModelFields = {
  report_id?: string;
  form_id?: string;
  intent?: 'report' | 'form';
};

const formModelWithLegacyFields = (fm: FormModel) => fm as FormModel & LegacyFormModelFields;

/** Resolves resource type, preferring `resource` and falling back to legacy `intent`. */
export const getFormModelResourceType = (fm: FormModel): 'form' | 'report' | undefined =>
  fm.resource?.type ?? formModelWithLegacyFields(fm).intent;

/** Resolves resource id, preferring `resource` and falling back to legacy `form_id`/`report_id`. */
export const getFormModelResourceId = (
  fm: FormModel,
  resourceType?: 'form' | 'report'
): string | undefined => {
  const legacy = formModelWithLegacyFields(fm);
  if (resourceType === 'form') return legacy.form_id;
  if (resourceType === 'report') return legacy.report_id;
  return fm.resource?.id;
};

export const fmHasSourceId = (fm: FormModel) => !!getFormModelResourceId(fm);
export const fmHasResourceType = (fm: FormModel) => !!getFormModelResourceType(fm);
export const isFormModelReport = (fm: FormModel) => getFormModelResourceType(fm) === 'report';
export const isFormModelForm = (fm: FormModel) => getFormModelResourceType(fm) === 'form';

function formModelsFactory() {
  async function getFormModelByFormId(formId: string) {
    const formModels = await collectionsService.formModels.getDocs(
      where('resource.id', '==', formId)
    );
    return formModels.length > 0 ? formModels[0] : null;
  }

  async function getFormModel(formModelId: string) {
    return collectionsService.formModels.getDoc(formModelId);
  }

  async function getFormModelByFormIds(
    formIds: string[],
    onProgress?: (data: FormModel[]) => void
  ) {
    const fetchedFormModels = await collectionsService.formModels.getDocsInBatches(
      ['resource.id', 'in', formIds],
      undefined,
      onProgress
    );

    return fetchedFormModels.flat();
  }

  async function getFormModelsByIds(ids: string[], onProgress?: (data: FormModel[]) => void) {
    const fetchedFormModels = await collectionsService.formModels.getDocsInBatches(
      ['id', 'in', ids],
      undefined,
      onProgress
    );

    return fetchedFormModels.flat();
  }

  return {
    getFormModelByFormId,
    getFormModelByFormIds,
    getFormModelsByIds,
    getFormModel
  };
}

export const formModelsService = formModelsFactory();
